/**
 * EmployeeRosterPage — /projects/:projectId/workforce
 * Demo: /manage-team (members table with search, filters and status) and /teams (member cards). Built on ListPage:
 * summary numbers, a toolbar with search, status pills and the scope / layout switches, then a DataTable or a card
 * grid. "Plan hire" continues in the project conversation, where hires are proposed and approved.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, LayoutGrid, Plus, Rows, Users } from 'lucide-react';
import { Badge, Button, Callout, EmptyState, Skeleton } from '../../components/ui';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { useEmployees, useWorkforceSummary } from '../../hooks/workforce';
import { useProject, useProjectCommandCenter } from '../../hooks/useProjects.js';
import { conversationHref } from '../projects/project-utils.js';
import { LayoutSwitcher, usePref, type EmployeeView } from './shared';
import { rosterStatCells } from './roster/FleetSummaryStrip.js';
import { RosterToolbar, type RosterFilter } from './roster/RosterToolbar.js';
import { RosterTable } from './roster/RosterTable.js';
import { RosterCards } from './roster/RosterCards.js';
import {
  buildProjectWorkforceLinks,
  isLinkedToProject,
  planWorkforcePrompt,
  toProjectScopedEmployee,
} from './project-context.js';

type RosterLayout = 'table' | 'cards';
type RosterScope = 'project' | 'all';

function matches(emp: EmployeeView, filter: RosterFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'attn') return emp.attn || emp.status === 'blocked' || emp.status === 'setup';
  return emp.status === filter;
}

function matchesSearch(emp: EmployeeView, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    emp.name.toLowerCase().includes(needle) ||
    emp.role.toLowerCase().includes(needle) ||
    (emp.team ?? '').toLowerCase().includes(needle) ||
    (emp.project ?? '').toLowerCase().includes(needle) ||
    (emp.workstream ?? '').toLowerCase().includes(needle)
  );
}

export const EmployeeRosterPage: React.FC = () => {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const { employees, isLoading, error, refetch } = useEmployees();
  const { data: summary } = useWorkforceSummary();
  const { data: project, isLoading: projectLoading, error: projectError } = useProject(projectId);
  const { data: commandCenter } = useProjectCommandCenter(projectId);

  const [layout, setLayout] = usePref<RosterLayout>('rosterLayout', 'table');
  const [scope, setScope] = usePref<RosterScope>('rosterScope', 'project');
  const [, setSelectedEmployeeId] = usePref<string>('selectedEmployeeId', '');
  const [filter, setFilter] = useState<RosterFilter>('all');
  const [search, setSearch] = useState('');

  const projectLinks = useMemo(() => buildProjectWorkforceLinks(commandCenter), [commandCenter]);
  const projectScopedEmployees = useMemo(
    () =>
      employees.map((employee) =>
        toProjectScopedEmployee(employee, project?.name ?? 'this project', isLinkedToProject(employee, projectLinks)),
      ),
    [employees, project?.name, projectLinks],
  );
  const assignedEmployees = useMemo(
    () => projectScopedEmployees.filter((employee) => isLinkedToProject(employee, projectLinks)),
    [projectScopedEmployees, projectLinks],
  );
  const scopedEmployees = scope === 'project' ? assignedEmployees : projectScopedEmployees;

  const counts = useMemo<Record<RosterFilter, number>>(() => {
    const c: Record<RosterFilter, number> = {
      all: scopedEmployees.length,
      attn: 0,
      working: 0,
      available: 0,
      blocked: 0,
      paused: 0,
    };
    for (const e of scopedEmployees) {
      if (e.attn || e.status === 'blocked' || e.status === 'setup') c.attn++;
      if (e.status === 'working') c.working++;
      else if (e.status === 'available') c.available++;
      else if (e.status === 'blocked') c.blocked++;
      else if (e.status === 'paused') c.paused++;
    }
    return c;
  }, [scopedEmployees]);

  const filtered = useMemo(
    () => scopedEmployees.filter((e) => matches(e, filter) && matchesSearch(e, search)),
    [scopedEmployees, filter, search],
  );

  const openPlanConversation = () => {
    if (!project) return;
    navigate(conversationHref(project, planWorkforcePrompt(project)));
  };

  const hrefFor = (employeeId: string) => `/projects/${projectId}/workforce/${employeeId}`;
  const openEmployee = (employeeId: string) => {
    setSelectedEmployeeId(employeeId);
    navigate(hrefFor(employeeId));
  };

  if (projectLoading) {
    return (
      <div className="space-y-5" aria-busy="true">
        <Skeleton height={28} width="30%" />
        <Skeleton height={260} rounded="rounded-xl" />
      </div>
    );
  }

  if (projectError || !project || !projectId) {
    return (
      <ListPage
        title="Workforce"
        breadcrumbs={[{ label: 'Projects', to: '/projects' }]}
        helpContent={PAGE_HELP['workforce']}
        alert={
          <Callout
            tone="warning"
            icon={AlertTriangle}
            title="Open workforce from a project"
            actions={
              <Button size="sm" variant="secondary" onClick={() => navigate('/projects')}>
                Back to projects
              </Button>
            }
          >
            Assignments stay tied to project threads and decisions, so the roster opens from a project.
          </Callout>
        }
      >
        {null}
      </ListPage>
    );
  }

  const planHire = (
    <Can policy="CanManageWorkforce">
      <Button onClick={openPlanConversation}>
        <Plus size={14} /> Plan hire
      </Button>
    </Can>
  );

  const toolbar = (
    <RosterToolbar
      filter={filter}
      setFilter={setFilter}
      counts={counts}
      searchQuery={search}
      setSearchQuery={setSearch}
      right={
        <>
          <LayoutSwitcher
            value={scope}
            ariaLabel="Roster scope"
            onChange={(next) => {
              setScope(next);
              setFilter('all');
            }}
            options={[
              { value: 'project', label: `Linked ${assignedEmployees.length}` },
              { value: 'all', label: `All reusable ${employees.length}` },
            ]}
          />
          <LayoutSwitcher
            value={layout}
            ariaLabel="Roster layout"
            onChange={setLayout}
            options={[
              { value: 'table', label: 'Table', icon: Rows },
              { value: 'cards', label: 'Cards', icon: LayoutGrid },
            ]}
          />
        </>
      }
    />
  );

  const empty = (
    <EmptyState
      icon={Users}
      title={scope === 'project' ? 'No employees linked to this project' : 'No employees yet'}
      description={
        scope === 'project'
          ? 'Plan workforce in the project conversation to assign reusable employees or propose new hires through decision cards.'
          : (PAGE_HELP['workforce']?.emptyState?.description ??
            'Hire an employee to give the project a durable AI collaborator.')
      }
      learnItems={PAGE_HELP['workforce']?.emptyState?.learnItems}
      action={planHire}
    />
  );

  const noMatches = (
    <EmptyState
      icon={Users}
      title="No employees match this filter"
      description="Try another status or clear the search."
      action={
        <Button
          variant="secondary"
          onClick={() => {
            setFilter('all');
            setSearch('');
          }}
        >
          Show all
        </Button>
      }
    />
  );

  const showCards = layout === 'cards' && filtered.length > 0;

  return (
    <ListPage
      title="Workforce"
      subtitle={`Project: ${project.name}. Employees are reusable across projects. This list shows who is linked here and who is available to propose.`}
      breadcrumbs={[
        { label: 'Projects', to: '/projects' },
        { label: project.name, to: `/projects/${project.id}` },
      ]}
      helpContent={PAGE_HELP['workforce']}
      actions={
        <>
          <Badge variant="info">{assignedEmployees.length} linked</Badge>
          {planHire}
        </>
      }
      stats={
        !isLoading && scopedEmployees.length > 0
          ? rosterStatCells(
              scopedEmployees,
              typeof summary === 'object' && summary ? (summary as { weeklyCost?: number }).weeklyCost : undefined,
              typeof summary === 'object' && summary
                ? (summary as { weeklyCostTrend?: number }).weeklyCostTrend
                : undefined,
            )
          : undefined
      }
      alert={
        error ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Failed to load workforce"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Something went wrong fetching the employees. Check your connection and try again.
          </Callout>
        ) : undefined
      }
      toolbar={scopedEmployees.length > 0 || isLoading ? toolbar : undefined}
      variant={showCards ? 'grid' : 'table'}
      footer={
        scopedEmployees.length > 0 ? (
          <p className="text-sm text-muted">
            Showing {filtered.length} of {scopedEmployees.length}
          </p>
        ) : undefined
      }
    >
      {error ? null : showCards ? (
        <RosterCards
          employees={filtered}
          hrefFor={hrefFor}
          projectName={project.name}
          linkedEmployeeIds={projectLinks}
        />
      ) : (
        <RosterTable
          employees={filtered}
          onOpen={openEmployee}
          hrefFor={hrefFor}
          projectName={project.name}
          linkedEmployeeIds={projectLinks}
          loading={isLoading}
          empty={scopedEmployees.length === 0 ? empty : noMatches}
        />
      )}
    </ListPage>
  );
};
